import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as AuthApi from '@/api/auth';
import { ApiHttpError } from '@/api/client';
import LanguageSwitch from '@/components/LanguageSwitch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function LoginPage() {
  const { t } = useTranslation(['auth', 'errors']);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const loginMutation = useMutation({
    mutationFn: AuthApi.login,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      const next = params.get('next') ?? '/dashboard';
      navigate(next, { replace: true });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiHttpError) {
        if (err.status === 401) setErrorMsg(t('auth:errors.invalidCredentials'));
        else if (err.status === 429) setErrorMsg(t('auth:errors.rateLimited'));
        else setErrorMsg(t('errors:unknown'));
      } else {
        setErrorMsg(t('errors:network'));
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    loginMutation.mutate({ username, password });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <h2 className="text-2xl font-semibold leading-none tracking-tight">
            {t('auth:login.title')}
          </h2>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t('auth:login.username')}</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth:login.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            {errorMsg && (
              <div role="alert" className="text-sm text-destructive">
                {errorMsg}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? t('auth:login.submitting') : t('auth:login.submit')}
            </Button>
          </form>
          <div className="mt-6 flex justify-center">
            <LanguageSwitch />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
